import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MerchantsService } from '../../core/services/merchants.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmModalComponent } from '../../shared/modals/confirm-modal/confirm-modal.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CreateBookingModalComponent } from '../../shared/modals/create-booking-modal/create-booking-modal.component';
import { ChangeData, NgxIntlTelInputModule } from 'untold-ngx-intl-tel-input';
import { addMonths, format, isValid, parseISO } from 'date-fns';
import { Subscription, filter, forkJoin } from 'rxjs';
import { UpgradePlanModalComponent } from '../../shared/modals/upgrade-plan-modal/upgrade-plan-modal.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { IntlService } from '../../core/services/intl.service';
import { StoresService } from '../../core/services/stores.service';
import { UserService } from '../../core/services/user.service';
import { NgClass, NgTemplateOutlet, CurrencyPipe, DatePipe } from '@angular/common';
import { ClientLoyaltyScoreComponent } from '../../shared/client-loyalty-score/client-loyalty-score.component';
import { BsDropdownDirective, BsDropdownToggleDirective, BsDropdownMenuDirective } from 'ngx-bootstrap/dropdown';
import { LoaderComponent } from '../../shared-with-client/loader/loader.component';
import { IfCountryDirective } from '../../shared/directives/if-country.directive';
import { TextInputComponent } from '../../shared-with-client/text-input/text-input.component';
import { PhoneInputComponent } from '../../shared-with-client/phone-input/phone-input.component';
import { DropdownInputV2Component } from '../../shared-with-client/dropdown-input-v2/dropdown-input-v2.component';
import { BsDatepickerInputDirective, BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { AlertComponent } from '../../shared-with-client/alert/alert.component';
import { SubmitBtnComponent } from '../../shared-with-client/submit-btn/submit-btn.component';
import { UpgradeButtonComponent } from '../../shared-with-client/upgrade-button/upgrade-button.component';
import { DropdownInputComponent } from '../../shared-with-client/dropdown-input/dropdown-input.component';
import { DateFixPipe } from '../../shared-with-client/pipes/date-fix.pipe';
import { ClientSalesComponent } from '../client-sales/client-sales.component';
import { ClientAttachmentsComponent } from '../client-attachments/client-attachments.component';
import { CalendarService } from '../services/calendar.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-client-form',
    templateUrl: './client-form.component.html',
    styleUrls: ['./client-form.component.scss'],
    imports: [
    ClientLoyaltyScoreComponent,
    BsDropdownDirective,
    BsDropdownToggleDirective,
    BsDropdownMenuDirective,
    NgClass,
    LoaderComponent,
    IfCountryDirective,
    FormsModule,
    ReactiveFormsModule,
    TextInputComponent,
    NgxIntlTelInputModule,
    PhoneInputComponent,
    DropdownInputV2Component,
    BsDatepickerInputDirective,
    BsDatepickerDirective,
    AlertComponent,
    SubmitBtnComponent,
    UpgradeButtonComponent,
    NgTemplateOutlet,
    DropdownInputComponent,
    CurrencyPipe,
    DatePipe,
    DateFixPipe,
    TranslatePipe,
    ClientSalesComponent,
    ClientAttachmentsComponent
],
    standalone: true,
})
export class ClientFormComponent implements OnInit {
    public client;
    public pastBookings = [];
    public futureBookings = [];
    public futureBookingsPage = 1;
    public pastBookingsPage = 1;
    public bookingsPerPage = 10;
    public futureBookingsHasMore = false;
    public pastBookingsHasMore = false;
    public loadingBookings = false;
    public loadingMoreFutureBookings = false;
    public loadingMorePastBookings = false;
    public store;

    public form: UntypedFormGroup;
    public customFieldsForm: UntypedFormGroup;
    public showForm = false;
    public openedFromBooking = false;

    public confirmModal: BsModalRef;

    public errorMsg;
    public submitAttempted = false;
    public saving;
    public savingCustomFields;

    public slideNum = 1;
    public currentMerchant;

    public stats;
    public targetGroupName;
    public merchantCustomFields;
    public merchantClientCustomFieldsValue;
    public loadingExtraFields = true;

    public loading = true;

    timeFormat: string;

    public merchantSubscription: Subscription;
    public calendarRefreshSubscription: Subscription;

    public genderOptions = [];

    constructor(
        private fb: UntypedFormBuilder,
        private merchantsService: MerchantsService,
        private storesService: StoresService,
        private userService: UserService,
        public bsModalRef: BsModalRef,
        private modalService: BsModalService,
        private translate: TranslateService,
        public intlService: IntlService,
        private calendarService: CalendarService,
    ) {}

    get freemiumMode(): boolean {
        return this.currentMerchant?.plan === 'free' || this.currentMerchant?.plan === null || this.currentMerchant?.plan === 'canceled';
    }

    shouldHideGeneralTab(): boolean {
        return this.openedFromBooking && !this.canShowClientContacts();
    }

    canShowClientContacts(): boolean {
        return this.userService.canShowClientContacts(this.store);
    }

    canCreateBooking(): boolean {
        if (this.userService.currentUser.value?.role === 'provider' && this.store?.allowProvidersToCreateBookings === false) {
            return false;
        }

        return true;
    }

    ngOnInit() {
        this.timeFormat = this.intlService.getTimeFormat();

        if (this.shouldHideGeneralTab()) {
            this.slideNum = 2;
        }

        this.genderOptions = [
            { value: 'male', label: this.translate.instant('GENERAL.gender.male') },
            { value: 'female', label: this.translate.instant('GENERAL.gender.female') },
            { value: 'other', label: this.translate.instant('GENERAL.gender.other') },
        ];

        this.merchantSubscription = this.merchantsService.activeMerchant.pipe(filter((v) => v !== null)).subscribe((res) => {
            this.currentMerchant = res;
            this.calculateTargetGroupName();
            this.getMerchantCustomFields();
        });

        if (this.client) {
            this.loadClientBookings();
        }

        this.calendarRefreshSubscription = this.calendarService.refreshCalendarObservable.subscribe(() => {
            if (this.client) {
                this.loadClientBookings();
            }
        });
    }

    getMerchantCustomFields(data = null) {
        this.merchantsService.getMerchantCustomFields(this.currentMerchant.slug).subscribe((res) => {
            this.merchantCustomFields = res;
            if (this.merchantCustomFields.length) {
                this.getMerchantClientCustomFieldsValue();
            } else {
                this.generateForm(this.client);
            }
        });
    }

    getMerchantClientCustomFieldsValue() {
        this.merchantsService.getClientCustomFields(this.client.id).subscribe((res) => {
            this.merchantClientCustomFieldsValue = res;
            this.loadingExtraFields = false;
            this.generateForm(this.client);
        });
    }

    calculateTargetGroupName() {
        this.targetGroupName = '';
        if (this.currentMerchant) {
            const dateToCompare = addMonths(new Date(), -this.currentMerchant.marketingAudienceMonthsConfig);

            if (
                (!this.client.lastVisit || new Date(this.client.lastVisit) >= addMonths(new Date(), -1)) &&
                new Date(this.client.createdAt) >= addMonths(new Date(), -1)
            ) {
                this.targetGroupName = this.translate.instant('DASHBOARD.clientForm.newClient');
            } else if (this.client.lastVisit && new Date(this.client.lastVisit) >= dateToCompare) {
                this.targetGroupName = this.translate.instant('DASHBOARD.clientForm.activeClient');
            } else if (this.client.lastVisit && new Date(this.client.lastVisit) < dateToCompare) {
                this.targetGroupName = this.translate.instant('DASHBOARD.clientForm.inactiveClient');
            }
        }
    }
    generateForm(data?) {
        this.loading = false;
        this.form = this.fb.group({
            name: [(data && this.generateFullName(data)) || '', [Validators.required]],
            dob: [(data && data.dob && parseISO(data.dob)) || null, [this.dateValidator]],
            gender: [(data && data.gender && this.genderOptions.find((v) => v.value === data.gender)) || null, []],
            phoneNumber: [(data && data.phoneNumber) || ''],
            email: [(data && data.email) || '', [Validators.email]],
            vatNumber: [(data && data.vatNumber) || '', []],
            notes: [(data && data.notes) || ''],
            subscribed: [data && data.unsubscribed === false ? true : false],
            notifyClient: [false],
        });
    }

    dateValidator(c: AbstractControl): { [key: string]: boolean } | null {
        if (c.pristine || !c.value) {
            return null;
        }
        if (!isValid(c.value)) {
            return { invalidDate: true };
        }
        return null;
    }

    generateFullName(data) {
        let fullName = '';
        if (data.firstName) {
            fullName += data.firstName;
        }
        if (data.lastName) {
            fullName += ` ${data.lastName}`;
        }
        return fullName;
    }

    deleteClient() {
        const initialState = {
            question: this.translate.instant('GENERAL.areYouSure'),
        };
        this.confirmModal = this.modalService.show(ConfirmModalComponent, { initialState, class: 'second' });
        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            if (reason && typeof reason === 'boolean') {
                this.merchantsService.deleteClient(this.client.id).subscribe(() => {
                    this.modalService.setDismissReason('delete-client');
                    this.bsModalRef.hide();
                });
            }
            modalCallback.unsubscribe();
        });
    }

    cancelEdit() {
        this.generateForm(this.client);
        this.showForm = false;
    }

    cancelEditMerchantCustomFields() {
        this.customFieldsForm = null;
    }

    editMerchantCustomFields() {
        this.savingCustomFields = false;
        this.customFieldsForm = this.fb.group({});
        this.merchantCustomFields
            .filter((v) => !v.key)
            .forEach((field) => {
                const value = this.merchantClientCustomFieldsValue?.[field.id] || null;
                if (field.type === 'date' && value) {
                    this.customFieldsForm.addControl('_' + field.id, new UntypedFormControl(parseISO(value)));
                } else {
                    this.customFieldsForm.addControl('_' + field.id, new UntypedFormControl(value));
                }
                this.customFieldsForm.updateValueAndValidity();
            });
    }

    saveCustomFields() {
        this.savingCustomFields = true;
        const payload = {};
        Object.keys(this.customFieldsForm.controls).forEach((key) => {
            let value: string = this.customFieldsForm.get(key).value;
            if (value && (value as any) instanceof Date) {
                value = format(value as any, 'yyyy-MM-dd');
            }
            payload[key.substring(1)] = value;
        });
        this.merchantsService.updateClientCustomFields(this.client.id, payload).subscribe((res) => {
            this.savingCustomFields = false;
            this.merchantClientCustomFieldsValue = res;
            this.customFieldsForm = null;

            if (this.openedFromBooking) {
                this.client.customFields = res;
                this.modalService.setDismissReason(this.client);
                this.bsModalRef.hide();
            }
        });
    }

    addClient() {
        this.submitAttempted = true;
        this.errorMsg = null;

        if (!this.form.valid) {
            return false;
        }

        this.saving = true;

        const email = this.form.get('email').value === '' ? null : this.form.get('email').value;

        const fullName = this.form.get('name').value?.split(' ');
        const firstName = fullName[0];
        let lastName = '';
        if (fullName.length > 1) {
            lastName = fullName.slice(1).join(' ');
        }

        let phoneNumber = this.form.get('phoneNumber').value === '' ? '' : this.form.get('phoneNumber').value;
        phoneNumber = this.parseFieldToPhoneNumber(phoneNumber);

        const notifyClient = this.form.get('notifyClient').value === '' ? false : this.form.get('notifyClient').value;

        const vatNumber = this.form.get('vatNumber').value || '';
        const notes = this.form.get('notes').value || '';
        const gender = this.form.get('gender').value || '';
        let dob = this.form.get('dob').value;

        if (dob) {
            dob = format(dob, 'yyyy-MM-dd');
        }

        const payload = {
            firstName,
            lastName,
            email,
            phoneNumber,
            notify: notifyClient,
            unsubscribed: !this.form.get('subscribed').value,
            vatNumber,
            notes,
            dob: dob ? dob : '',
            gender: gender?.value,
        };

        if (this.client) {
            this.merchantsService.updateClient(this.client.id, payload).subscribe(
                (res) => {
                    this.client = res;
                    this.saving = false;
                    this.showForm = false;

                    this.storesService.refreshClientList.next(true);
                    if (this.openedFromBooking) {
                        this.modalService.setDismissReason(res);
                        this.bsModalRef.hide();
                    }
                },
                (err) => {
                    this.errorMsg = err.errMsg;
                    this.saving = false;
                },
            );
        } else {
            this.merchantsService.createClient(payload).subscribe(
                (res) => {
                    this.saving = false;
                    this.modalService.setDismissReason(res);
                    this.bsModalRef.hide();
                },
                (err) => {
                    this.errorMsg = err.errMsg;
                    this.saving = false;
                },
            );
        }
    }

    addToBlacklist() {
        this.saving = true;
        this.merchantsService.addToBlacklist(this.client.id).subscribe(
            (res) => {
                this.saving = false;
                this.modalService.setDismissReason(res);
                this.storesService.refreshClientList.next(true);
                this.bsModalRef.hide();
            },
            (err) => {
                this.errorMsg = err.errMsg;
                this.saving = false;
            },
        );
    }

    removeFromBlacklist() {
        this.saving = true;
        this.merchantsService.removeFromBlacklist(this.client.id).subscribe(
            (res) => {
                this.saving = false;
                this.modalService.setDismissReason(res);
                this.bsModalRef.hide();
            },
            (err) => {
                this.errorMsg = err.errMsg;
                this.saving = false;
            },
        );
    }

    close() {
        this.bsModalRef.hide();
    }

    newBooking() {
        const initialState = {
            day: format(new Date(), 'yyyy-MM-dd'),
            existingClient: this.client,
            store: this.store,
            merchant: this.currentMerchant,
        };

        this.modalService.show(CreateBookingModalComponent, { initialState, class: 'modal-xl' });

        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            modalCallback.unsubscribe();
        });
        this.modalService.setDismissReason('new-booking');
        this.close();
    }

    canOpenBooking(booking) {
        return booking?.id && !booking.deletedAt;
    }

    openBooking(booking) {
        if (!this.canOpenBooking(booking)) {
            return;
        }

        this.calendarService.editBooking(
            this.currentMerchant,
            this.store,
            { data: booking },
            undefined,
            { modalClass: 'second modal-xl', fetchFullBooking: true },
        );

        const thisModalNumber = this.modalService.getModalsCount();
        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            if (thisModalNumber - 1 !== this.modalService.getModalsCount()) {
                return;
            }

            modalCallback.unsubscribe();
            if (reason === 'saved') {
                this.loadClientBookings();
            }
        });
    }

    parseFieldToPhoneNumber(value: ChangeData): string {
        return value?.e164Number ?? '';
    }

    loadClientBookings() {
        this.loadingBookings = true;
        forkJoin({
            future: this.merchantsService.getClientBookings(this.client.id, 'future', 1, this.bookingsPerPage),
            past: this.merchantsService.getClientBookings(this.client.id, 'past', 1, this.bookingsPerPage),
        }).subscribe(({ future, past }) => {
            this.applyFutureBookingsResponse(future, true);
            this.applyPastBookingsResponse(past, true);
            this.loadingBookings = false;
        });
    }

    loadMoreFutureBookings() {
        if (!this.futureBookingsHasMore || this.loadingMoreFutureBookings) {
            return;
        }

        this.loadingMoreFutureBookings = true;
        this.merchantsService.getClientBookings(this.client.id, 'future', this.futureBookingsPage + 1, this.bookingsPerPage).subscribe((res) => {
            this.applyFutureBookingsResponse(res, false);
            this.loadingMoreFutureBookings = false;
        });
    }

    loadMorePastBookings() {
        if (!this.pastBookingsHasMore || this.loadingMorePastBookings) {
            return;
        }

        this.loadingMorePastBookings = true;
        this.merchantsService.getClientBookings(this.client.id, 'past', this.pastBookingsPage + 1, this.bookingsPerPage).subscribe((res) => {
            this.applyPastBookingsResponse(res, false);
            this.loadingMorePastBookings = false;
        });
    }

    applyFutureBookingsResponse(res, reset: boolean) {
        const newBookings = res?.data || [];
        this.futureBookings = reset ? newBookings : [...this.futureBookings, ...newBookings];
        this.futureBookingsPage = res?.pagination?.page || 1;
        this.futureBookingsHasMore = this.futureBookingsPage < (res?.pagination?.totalPages || 1);

        if (res?.stats) {
            this.stats = res.stats;
        }
    }

    applyPastBookingsResponse(res, reset: boolean) {
        const newBookings = res?.data || [];
        this.pastBookings = reset ? newBookings : [...this.pastBookings, ...newBookings];
        this.pastBookingsPage = res?.pagination?.page || 1;
        this.pastBookingsHasMore = this.pastBookingsPage < (res?.pagination?.totalPages || 1);

        if (res?.stats) {
            this.stats = res.stats;
        }
    }

    hasNoBookings(): boolean {
        return this.futureBookings.length === 0 && this.pastBookings.length === 0;
    }

    editClient() {
        this.showForm = !this.showForm;
    }

    upgradePro(reason) {
        if (this.currentMerchant.plan === 'free' || this.currentMerchant.plan === null || this.currentMerchant.plan === 'canceled') {
            const initialState = {
                reason: reason,
            };

            this.modalService.show(UpgradePlanModalComponent, { class: 'modal-md', initialState });

            this.bsModalRef.hide();
        }
    }
}
